import React, { useState, useEffect } from 'react';
import { 
  FileText, Download, Printer, Calendar, Users, DollarSign, 
  Loader2, RefreshCw, AlertTriangle, CheckCircle2, Archive
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useCompany } from '../context/CompanyContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PayrollReportsPage = () => {
  const { companyId, selectedCompany } = useCompany();
  
  const [activeTab, setActiveTab] = useState('w2');
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  
  // W-2 Data
  const [w2Data, setW2Data] = useState({ employees: [], totals: {} });
  
  // YTD Data
  const [ytdData, setYtdData] = useState([]);
  
  // 941/DE-9 Data
  const [form941Data, setForm941Data] = useState(null);
  const [formDE9Data, setFormDE9Data] = useState(null);

  const tabs = [
    { id: 'w2', label: 'W-2 Forms', icon: FileText },
    { id: 'ytd', label: 'YTD Report', icon: DollarSign },
    { id: '941', label: 'Form 941', icon: FileText },
    { id: 'de9', label: 'Form DE-9', icon: FileText },
    { id: 'history', label: 'Payroll History', icon: Calendar }
  ];

  const years = [2024, 2025, 2026, 2027];
  const quarters = [
    { value: 1, label: 'Q1 (Jan-Mar)' },
    { value: 2, label: 'Q2 (Apr-Jun)' },
    { value: 3, label: 'Q3 (Jul-Sep)' },
    { value: 4, label: 'Q4 (Oct-Dec)' }
  ];

  // Fetch Form 941 data
  const fetch941Data = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/form-941?year=${selectedYear}&quarter=${selectedQuarter}&company_id=${encodeURIComponent(companyId || 'default')}`);
      if (res.ok) {
        const data = await res.json();
        setForm941Data(data);
        toast.success('Form 941 data loaded');
      } else {
        toast.error('Failed to load Form 941 data');
      }
    } catch (err) {
      console.error('Failed to fetch 941 data:', err);
      toast.error('Error loading Form 941 data');
    } finally {
      setLoading(false);
    }
  };

  // Fetch Form DE-9 data
  const fetchDE9Data = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/form-de9?year=${selectedYear}&quarter=${selectedQuarter}&company_id=${encodeURIComponent(companyId || 'default')}`);
      if (res.ok) {
        const data = await res.json();
        setFormDE9Data(data);
        toast.success('Form DE-9 data loaded');
      } else {
        toast.error('Failed to load Form DE-9 data');
      }
    } catch (err) {
      console.error('Failed to fetch DE-9 data:', err);
      toast.error('Error loading Form DE-9 data');
    } finally {
      setLoading(false);
    }
  };

  // Download Form 941 PDF
  const download941PDF = () => {
    window.open(`${API_URL}/api/reports/form-941-pdf?year=${selectedYear}&quarter=${selectedQuarter}&company_id=${encodeURIComponent(companyId || 'default')}`, '_blank');
  };

  // Download Form DE-9 PDF
  const downloadDE9PDF = () => {
    window.open(`${API_URL}/api/reports/form-de9-pdf?year=${selectedYear}&quarter=${selectedQuarter}&company_id=${encodeURIComponent(companyId || 'default')}`, '_blank');
  };

  const fetchW2Data = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reports/w2-list?year=${selectedYear}&company_id=${encodeURIComponent(companyId || 'default')}`);
      if (res.ok) {
        const data = await res.json();
        setW2Data(data);
      }
    } catch (err) {
      console.error('Failed to fetch W-2 data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchYTDData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/ca-payroll/ytd-summary?year=${selectedYear}&company_id=${companyId || ''}`);
      if (res.ok) {
        setYtdData(await res.json());
      }
    } catch (err) {
      console.error('Failed to fetch YTD data:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadW2 = async (employeeId, employeeName) => {
    // Use the IRS-compliant complete set endpoint with Copy B and C (employee copies)
    window.open(`${API_URL}/api/reports/w2-complete-set/${employeeId}?year=${selectedYear}&copies=B,C`, '_blank');
  };

  const downloadW2WithCopies = (employeeId, copies) => {
    window.open(`${API_URL}/api/reports/w2-complete-set/${employeeId}?year=${selectedYear}&copies=${copies}`, '_blank');
  };

  const downloadAllW2s = () => {
    window.open(`${API_URL}/api/reports/w2-bulk-pdf?year=${selectedYear}&company_id=${encodeURIComponent(companyId || 'default')}`, '_blank');
  };

  const downloadW2_4UP = () => {
    window.open(`${API_URL}/api/reports/w2-4up-pdf?year=${selectedYear}&company_id=${encodeURIComponent(companyId || 'default')}`, '_blank');
  };

  const downloadEFW2 = () => {
    window.open(`${API_URL}/api/reports/efw2-file?year=${selectedYear}`, '_blank');
  };

  const downloadEFW2Zip = () => {
    window.open(`${API_URL}/api/reports/efw2-zip?year=${selectedYear}`, '_blank');
  };

  // NEW: Download employer filing set (Copy A, D, 1)
  const downloadEmployerFilingSet = () => {
    window.open(`${API_URL}/api/reports/w2-employer-filing-set?year=${selectedYear}&company_id=${companyId || ''}`, '_blank');
  };

  // NEW: Download employee distribution set (Copy B, C, 2)
  const downloadEmployeeDistribution = () => {
    window.open(`${API_URL}/api/reports/w2-employee-copies?year=${selectedYear}&company_id=${companyId || ''}`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Payroll Reports</h1>
          <p className="text-slate-500">Generate W-2s, quarterly reports, and payroll summaries</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-500">Company:</span>
          <span className="font-medium text-sky-700">{selectedCompany?.name || 'Blue Skies Farm'}</span>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg">
        {tabs.map(tab => (
          <Button
            key={tab.id}
            variant={activeTab === tab.id ? 'default' : 'ghost'}
            size="sm"
            onClick={() => setActiveTab(tab.id)}
            className={activeTab === tab.id ? 'bg-white shadow-sm text-[#14213d] hover:bg-white' : ''}
          >
            <tab.icon className="w-4 h-4 mr-2" />
            {tab.label}
          </Button>
        ))}
      </div>

      {/* W-2 Forms Tab */}
      {activeTab === 'w2' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-sky-600" />
                  W-2 Wage and Tax Statement
                </CardTitle>
                <CardDescription>Generate annual W-2 forms for employees</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="border rounded-md p-2"
                  value={selectedYear}
                  onChange={e => setSelectedYear(parseInt(e.target.value))}
                >
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <Button onClick={fetchW2Data} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Load Data
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {w2Data.employees?.length > 0 ? (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-sky-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-sky-700">{w2Data.total_employees || w2Data.employees.length}</p>
                    <p className="text-sm text-slate-500">Employees</p>
                  </div>
                  <div className="p-4 bg-emerald-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-700">
                      ${(w2Data.totals?.total_wages || w2Data.employees.reduce((s, e) => s + e.wages, 0)).toLocaleString()}
                    </p>
                    <p className="text-sm text-slate-500">Total Wages</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-700">
                      ${(w2Data.totals?.total_federal_tax || w2Data.employees.reduce((s, e) => s + e.federal_tax, 0)).toLocaleString()}
                    </p>
                    <p className="text-sm text-slate-500">Federal Tax</p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-700">
                      ${(w2Data.totals?.total_state_tax || w2Data.employees.reduce((s, e) => s + e.state_tax, 0)).toLocaleString()}
                    </p>
                    <p className="text-sm text-slate-500">State Tax</p>
                  </div>
                </div>

                {/* Download Buttons */}
                <div className="space-y-4">
                  {/* IRS/SSA Filing Section */}
                  <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                    <h4 className="font-medium text-red-800 mb-2">IRS/SSA Filing (Employer)</h4>
                    <p className="text-sm text-red-600 mb-3">Copy A (RED) for SSA, Copy D for your records, Copy 1 for State</p>
                    <div className="flex gap-2 flex-wrap">
                      <Button variant="outline" onClick={downloadEmployerFilingSet} className="border-red-300 text-red-700 hover:bg-red-100" data-testid="download-employer-filing-set-btn">
                        <Download className="w-4 h-4 mr-2" /> Download Employer Filing Set
                      </Button>
                      <Button variant="outline" onClick={downloadEFW2} className="border-red-300 text-red-700 hover:bg-red-100" data-testid="download-efw2-txt-btn">
                        <FileText className="w-4 h-4 mr-2" /> EFW2 Electronic File
                      </Button>
                      <Button variant="outline" onClick={downloadEFW2Zip} className="border-red-300 text-red-700 hover:bg-red-100" data-testid="download-efw2-zip-btn">
                        <Archive className="w-4 h-4 mr-2" /> EFW2 ZIP (BSO Upload)
                      </Button>
                    </div>
                  </div>
                  
                  {/* Employee Distribution Section */}
                  <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
                    <h4 className="font-medium text-sky-800 mb-2">Employee Distribution</h4>
                    <p className="text-sm text-sky-600 mb-3">Copy B (Federal), Copy C (Records), Copy 2 (State) for each employee</p>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={downloadEmployeeDistribution} className="border-sky-300 text-sky-700 hover:bg-sky-100">
                        <Download className="w-4 h-4 mr-2" /> Download Employee Set (All)
                      </Button>
                      <Button variant="outline" onClick={downloadW2_4UP} className="border-sky-300 text-sky-700 hover:bg-sky-100">
                        <Printer className="w-4 h-4 mr-2" /> W-2 4-UP Format (Print)
                      </Button>
                    </div>
                  </div>
                  
                  {/* Legacy Options */}
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={downloadAllW2s}>
                      <Download className="w-4 h-4 mr-2" /> All W-2s (Standard PDF)
                    </Button>
                  </div>
                </div>

                {/* Employee List */}
                <table className="w-full text-sm">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-3">Employee</th>
                      <th className="text-left p-3">SSN</th>
                      <th className="text-right p-3">Wages (Box 1)</th>
                      <th className="text-right p-3">Fed Tax (Box 2)</th>
                      <th className="text-right p-3">SS Tax (Box 4)</th>
                      <th className="text-right p-3">Medicare (Box 6)</th>
                      <th className="text-right p-3">State Tax (Box 17)</th>
                      <th className="text-center p-3">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {w2Data.employees.map(emp => (
                      <tr key={emp.employee_id} className="border-b hover:bg-slate-50">
                        <td className="p-3 font-medium">{emp.name}</td>
                        <td className="p-3 text-slate-500 font-mono text-xs">{emp.ssn_masked}</td>
                        <td className="p-3 text-right">${emp.wages?.toLocaleString()}</td>
                        <td className="p-3 text-right text-red-600">${emp.federal_tax?.toLocaleString()}</td>
                        <td className="p-3 text-right">${emp.ss_tax?.toLocaleString()}</td>
                        <td className="p-3 text-right">${emp.medicare_tax?.toLocaleString()}</td>
                        <td className="p-3 text-right text-amber-600">${emp.state_tax?.toLocaleString()}</td>
                        <td className="p-3 text-center">
                          <Button variant="outline" size="sm" onClick={() => downloadW2(emp.employee_id, emp.name)}>
                            <Download className="w-4 h-4 mr-1" /> W-2
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {/* SSN Validation Notes */}
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> SSN Validation for Electronic Filing
                  </h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li>• SSN <strong>cannot</strong> have 666 as prefix - SSA will reject</li>
                    <li>• SSN <strong>cannot</strong> start with 9 - Reserved for ITINs</li>
                    <li>• Use <strong>000-00-0000</strong> if SSN unknown</li>
                    <li>• EIN must be <strong>9 digits without dashes</strong> for EFW2</li>
                  </ul>
                </div>

                {/* Distribution Instructions */}
                <div className="p-4 bg-sky-50 border border-sky-200 rounded-lg">
                  <h4 className="font-medium text-sky-800 mb-2">W-2 Distribution Instructions</h4>
                  <ul className="text-sm text-sky-700 space-y-1">
                    <li>• <strong>Copy B:</strong> Employee's federal tax return</li>
                    <li>• <strong>Copy C:</strong> Employee's records</li>
                    <li>• <strong>Copy 2:</strong> Employee's state/local return</li>
                    <li>• W-2s must be provided by <strong>January 31</strong></li>
                    <li>• <strong>EFW2:</strong> SSA electronic submission (Business Services Online)</li>
                  </ul>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No W-2 Data</h3>
                <p className="text-sm text-slate-500 mt-1">Select a year and click "Load Data" to generate W-2 forms</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* YTD Report Tab */}
      {activeTab === 'ytd' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-sky-600" />
                  Year-to-Date Summary
                </CardTitle>
                <CardDescription>Employee earnings and tax withholdings YTD</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="border rounded-md p-2"
                  value={selectedYear}
                  onChange={e => setSelectedYear(parseInt(e.target.value))}
                >
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <Button onClick={fetchYTDData} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Load Data
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {ytdData.length > 0 ? (
              <table className="w-full text-sm">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left p-3">Employee</th>
                    <th className="text-right p-3">YTD Gross</th>
                    <th className="text-right p-3">YTD Fed Tax</th>
                    <th className="text-right p-3">YTD State Tax</th>
                    <th className="text-right p-3">YTD SS</th>
                    <th className="text-right p-3">YTD Medicare</th>
                    <th className="text-right p-3">YTD Net</th>
                  </tr>
                </thead>
                <tbody>
                  {ytdData.map((emp, idx) => (
                    <tr key={idx} className="border-b hover:bg-slate-50">
                      <td className="p-3 font-medium">{emp.employee_name || emp.employee_id}</td>
                      <td className="p-3 text-right">${(emp.ytd_gross || 0).toLocaleString()}</td>
                      <td className="p-3 text-right text-red-600">${(emp.ytd_federal_tax || 0).toLocaleString()}</td>
                      <td className="p-3 text-right text-amber-600">${(emp.ytd_state_tax || 0).toLocaleString()}</td>
                      <td className="p-3 text-right">${(emp.ytd_ss_tax || 0).toLocaleString()}</td>
                      <td className="p-3 text-right">${(emp.ytd_medicare_tax || 0).toLocaleString()}</td>
                      <td className="p-3 text-right font-bold text-emerald-600">${(emp.ytd_net || 0).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12">
                <DollarSign className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No YTD Data</h3>
                <p className="text-sm text-slate-500 mt-1">Click "Load Data" to view YTD summary</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Form 941 Tab */}
      {activeTab === '941' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-sky-600" />
                  Form 941 - Quarterly Federal Tax Return
                </CardTitle>
                <CardDescription>Employer's quarterly federal tax report</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="border rounded-md p-2"
                  value={selectedYear}
                  onChange={e => setSelectedYear(parseInt(e.target.value))}
                >
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select
                  className="border rounded-md p-2"
                  value={selectedQuarter}
                  onChange={e => setSelectedQuarter(parseInt(e.target.value))}
                >
                  {quarters.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                </select>
                <Button onClick={fetch941Data} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Load Data
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {form941Data ? (
              <div className="space-y-4">
                <div className="p-6 bg-slate-50 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Form 941 - Q{form941Data.quarter} {form941Data.year}</h3>
                    <Button onClick={download941PDF} variant="outline">
                      <Download className="w-4 h-4 mr-2" /> Download PDF
                    </Button>
                  </div>
                  
                  {/* Employer Info */}
                  <div className="mb-4 p-3 bg-white rounded border">
                    <p className="text-sm text-slate-500">Employer</p>
                    <p className="font-bold">{form941Data.employer?.name}</p>
                    <p className="text-sm">EIN: {form941Data.employer?.ein}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">Line 1: Number of Employees</p>
                        <p className="text-xl font-bold">{form941Data.line_items?.line_1 || 0}</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">Line 2: Wages, Tips, Other Compensation</p>
                        <p className="text-xl font-bold">${(form941Data.line_items?.line_2 || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">Line 3: Federal Income Tax Withheld</p>
                        <p className="text-xl font-bold text-red-600">${(form941Data.line_items?.line_3 || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">Line 5a: Taxable Social Security Wages</p>
                        <p className="text-xl font-bold">${(form941Data.line_items?.line_5a_col1 || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">Line 5a: Social Security Tax</p>
                        <p className="text-xl font-bold">${(form941Data.line_items?.line_5a_col2 || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">Line 5c: Taxable Medicare Wages</p>
                        <p className="text-xl font-bold">${(form941Data.line_items?.line_5c_col1 || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">Line 5c: Medicare Tax</p>
                        <p className="text-xl font-bold">${(form941Data.line_items?.line_5c_col2 || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                      <div className="p-3 bg-sky-50 rounded border border-sky-200">
                        <p className="text-sm text-sky-600">Line 10: Total Taxes</p>
                        <p className="text-2xl font-bold text-sky-700">${(form941Data.line_items?.line_10 || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-amber-50 rounded border border-amber-200">
                  <p className="text-sm text-amber-700">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Form 941 is due by the last day of the month following the quarter end.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No Data Loaded</h3>
                <p className="text-sm text-slate-500 mt-1">Select year and quarter, then click "Load Data"</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Form DE-9 Tab */}
      {activeTab === 'de9' && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-sky-600" />
                  Form DE-9 - California Quarterly Tax Return
                </CardTitle>
                <CardDescription>California employer's quarterly contribution return</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="border rounded-md p-2"
                  value={selectedYear}
                  onChange={e => setSelectedYear(parseInt(e.target.value))}
                >
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select
                  className="border rounded-md p-2"
                  value={selectedQuarter}
                  onChange={e => setSelectedQuarter(parseInt(e.target.value))}
                >
                  {quarters.map(q => <option key={q.value} value={q.value}>{q.label}</option>)}
                </select>
                <Button onClick={fetchDE9Data} disabled={loading}>
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                  Load Data
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {formDE9Data ? (
              <div className="space-y-4">
                <div className="p-6 bg-slate-50 rounded-lg">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium">Form DE-9 - Q{formDE9Data.quarter} {formDE9Data.year}</h3>
                    <Button onClick={downloadDE9PDF} variant="outline">
                      <Download className="w-4 h-4 mr-2" /> Download PDF
                    </Button>
                  </div>
                  
                  {/* Employer Info */}
                  <div className="mb-4 p-3 bg-white rounded border">
                    <p className="text-sm text-slate-500">Employer</p>
                    <p className="font-bold">{formDE9Data.employer?.name}</p>
                    <p className="text-sm">EDD Account: {formDE9Data.employer?.edd_account}</p>
                    <p className="text-sm">FEIN: {formDE9Data.employer?.fein}</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">A. Total Subject Wages</p>
                        <p className="text-xl font-bold">${(formDE9Data.section_a?.total_subject_wages || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">B. UI Subject Wages</p>
                        <p className="text-xl font-bold">${(formDE9Data.section_a?.ui_wages || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                        <p className="text-xs text-slate-400">Rate: {((formDE9Data.section_a?.ui_rate || 0) * 100).toFixed(1)}%</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">C. UI Contributions</p>
                        <p className="text-xl font-bold text-amber-600">${(formDE9Data.section_a?.ui_contributions || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">D. SDI Subject Wages</p>
                        <p className="text-xl font-bold">${(formDE9Data.section_a?.sdi_wages || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">E. PIT Wages</p>
                        <p className="text-xl font-bold">${(formDE9Data.section_b?.pit_wages || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">F. PIT Withheld</p>
                        <p className="text-xl font-bold text-red-600">${(formDE9Data.section_b?.pit_withheld || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                      <div className="p-3 bg-white rounded border">
                        <p className="text-sm text-slate-500">SDI Withheld</p>
                        <p className="text-xl font-bold">${(formDE9Data.section_a?.sdi_withheld || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                      <div className="p-3 bg-sky-50 rounded border border-sky-200">
                        <p className="text-sm text-sky-600">Total Contribution Due</p>
                        <p className="text-2xl font-bold text-sky-700">${(formDE9Data.totals?.grand_total || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-4 gap-4">
                    <div className="p-2 bg-white rounded border text-center">
                      <p className="text-xs text-slate-500">UI Rate</p>
                      <p className="font-bold">{((formDE9Data.section_a?.ui_rate || 0.034) * 100).toFixed(1)}%</p>
                    </div>
                    <div className="p-2 bg-white rounded border text-center">
                      <p className="text-xs text-slate-500">ETT Rate</p>
                      <p className="font-bold">{((formDE9Data.section_a?.ett_rate || 0.001) * 100).toFixed(2)}%</p>
                    </div>
                    <div className="p-2 bg-white rounded border text-center">
                      <p className="text-xs text-slate-500">SDI Rate</p>
                      <p className="font-bold">{((formDE9Data.section_a?.sdi_rate || 0.009) * 100).toFixed(1)}%</p>
                    </div>
                    <div className="p-2 bg-white rounded border text-center">
                      <p className="text-xs text-slate-500">Employees</p>
                      <p className="font-bold">{formDE9Data.employee_count || 0}</p>
                    </div>
                  </div>
                </div>
                <div className="p-3 bg-amber-50 rounded border border-amber-200">
                  <p className="text-sm text-amber-700">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Form DE-9 is due quarterly to the California EDD. File online at e-Services for Business.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No Data Loaded</h3>
                <p className="text-sm text-slate-500 mt-1">Select year and quarter, then click "Load Data"</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payroll History Tab */}
      {activeTab === 'history' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-sky-600" />
              Payroll History
            </CardTitle>
            <CardDescription>View past payroll runs and payments</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12">
              <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600">Payroll History</h3>
              <p className="text-sm text-slate-500 mt-1">View and manage historical payroll runs</p>
              <Button className="mt-4" variant="outline">
                <RefreshCw className="w-4 h-4 mr-2" /> Load History
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PayrollReportsPage;
